import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import CarCard from "../../components/public/CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lowest subscription price = "Fra X kr." (rent) / sale_price (salg)
const rentFromPrice = (c) =>
  c.sub_price_12m || c.sub_price_6m || c.sub_price_3m || c.sub_price_1m || Infinity;
const salePrice = (c) => c.sale_price || c.listed_price || c.price || Infinity;

const SORT_OPTIONS_RENT = [
  { value: "default", label: "Anbefalede" },
  { value: "price_asc", label: "Pris: Lav → Høj" },
  { value: "price_desc", label: "Pris: Høj → Lav" },
  { value: "return_asc", label: "Returnerer snarest" },
  { value: "return_desc", label: "Returnerer senest" },
];
const SORT_OPTIONS_SALE = [
  { value: "default", label: "Anbefalede" },
  { value: "price_asc", label: "Pris: Lav → Høj" },
  { value: "price_desc", label: "Pris: Høj → Lav" },
];

// Compact desktop dropdown (label + value + chevron). Used for Sort + simple selects.
function FilterDropdown({ label, value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value) || options[0];
  const active = value !== null && value !== "default" && value !== undefined;
  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm border transition-all ${
          active
            ? "bg-graphite text-white border-graphite"
            : "bg-white text-graphite border-[#E5DED1] hover:border-graphite"
        }`}
        data-testid={`${testid}-trigger`}
      >
        <span className={`text-[10px] uppercase tracking-wider ${active ? "text-white/60" : "text-graphite/50"}`}>{label}</span>
        <span className="font-medium">{current.label}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""} ${active ? "text-white/70" : "text-graphite/50"}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-2 left-0 min-w-[220px] bg-white border border-[#E5DED1] rounded-xl shadow-xl overflow-hidden">
            {options.map((o) => (
              <button
                key={String(o.value)}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  o.value === value ? "bg-graphite text-white" : "text-graphite hover:bg-[#F7F3EC]"
                }`}
                data-testid={`${testid}-option-${o.value ?? "all"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Mobile drawer body — same filter controls but as full-width selects
function MobileFilterDrawer({ open, onClose, filters, setFilters, facets, isRent, sortOptions, activeCount, onReset, isPlatformLocked = false }) {
  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="mobile-filter-overlay" />
      <div className="absolute left-0 right-0 bottom-0 bg-[#F7F3EC] rounded-t-3xl max-h-[88vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#F7F3EC] border-b border-[#E5DED1] px-5 py-4 flex items-center justify-between z-10">
          <div className="w-10 h-1 bg-graphite/20 rounded-full absolute left-1/2 -translate-x-1/2 top-2"/>
          <h3 className="font-heading text-xl text-graphite">Filtre {activeCount > 0 && <span className="ml-1 text-sm text-copper">({activeCount})</span>}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center text-graphite" aria-label="Luk" data-testid="mobile-filter-close">
            <X size={18}/>
          </button>
        </div>
        <div className="p-5 space-y-5">
          {facets.categories.length > 0 && (
            <MobileSelect label="Kategori" value={filters.cat} onChange={(v) => setFilters({ ...filters, cat: v })} options={[{ value: null, label: "Alle" }, ...facets.categories.map((c) => ({ value: c, label: c }))]} testid="m-filter-category"/>
          )}
          {facets.fuels.length > 0 && (
            <MobileSelect label="Brændstof" value={filters.fuel} onChange={(v) => setFilters({ ...filters, fuel: v })} options={[{ value: null, label: "Alle" }, ...facets.fuels.map((c) => ({ value: c, label: c }))]} testid="m-filter-fuel"/>
          )}
          {facets.transmissions.length > 0 && (
            <MobileSelect label="Gearkasse" value={filters.trans} onChange={(v) => setFilters({ ...filters, trans: v })} options={[{ value: null, label: "Alle" }, ...facets.transmissions.map((c) => ({ value: c, label: c }))]} testid="m-filter-transmission"/>
          )}
          {isRent && (
            <>
              <MobileSelect label="Tilgængelighed" value={filters.availability} onChange={(v) => setFilters({ ...filters, availability: v })} options={[
                { value: null, label: "Alle" },
                { value: "available", label: "Ledige nu" },
                { value: "rented", label: "Udlejet" },
              ]} testid="m-filter-availability"/>
              {!isPlatformLocked && (
                <MobileSelect label="Lejetype" value={filters.platform} onChange={(v) => setFilters({ ...filters, platform: v })} options={[
                  { value: null, label: "Alle" },
                  { value: "gomore", label: "Dags leje" },
                  { value: "direct", label: "Langtid leje" },
                ]} testid="m-filter-platform"/>
              )}
            </>
          )}
          <MobileSelect label="Sortér" value={filters.sort} onChange={(v) => setFilters({ ...filters, sort: v })} options={sortOptions} testid="m-filter-sort"/>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E5DED1] p-4 flex gap-3">
          <button onClick={onReset} className="flex-1 px-5 py-3 rounded-full border border-graphite/40 text-graphite font-medium hover:bg-graphite/5 transition-colors" data-testid="mobile-filter-reset">
            Nulstil
          </button>
          <button onClick={onClose} className="flex-1 px-5 py-3 rounded-full bg-graphite text-white font-medium hover:bg-graphite-light transition-colors" data-testid="mobile-filter-apply">
            Vis biler
          </button>
        </div>
      </div>
    </div>
  );
}

function MobileSelect({ label, value, options, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-graphite/50 mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => (
          <button
            key={String(o.value)}
            onClick={() => onChange(o.value)}
            className={`px-3 py-2.5 rounded-xl text-sm border transition-all text-left ${
              value === o.value ? "bg-graphite text-white border-graphite" : "bg-white text-graphite border-[#E5DED1] hover:border-graphite"
            }`}
            data-testid={`${testid}-${o.value ?? "all"}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PublicListing({ type = "rent", platformLock = null, pageTitle = null, pageHeading = null, pageSub = null }) {
  const [data, setData] = useState({ cars: [], facets: { categories: [], fuels: [], transmissions: [] } });
  const [siteInfo, setSiteInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ cat: null, fuel: null, trans: null, availability: null, platform: platformLock, sort: "default" });
  const [mobileOpen, setMobileOpen] = useState(false);

  const isRent = type === "rent";
  const isPlatformLocked = platformLock !== null;

  // Keep locked platform in sync if prop changes
  useEffect(() => {
    if (isPlatformLocked) setFilters((f) => ({ ...f, platform: platformLock }));
  }, [platformLock, isPlatformLocked]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/cars?type=${type}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    axios.get(`${API}/public/site-info`).then((r) => setSiteInfo(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const list = data.cars.filter((c) => {
      if (filters.cat && c.category !== filters.cat) return false;
      if (filters.fuel && c.fuel_type !== filters.fuel) return false;
      if (filters.trans && c.transmission !== filters.trans) return false;
      if (isRent && filters.availability === "available" && c.is_rented_out) return false;
      if (isRent && filters.availability === "rented" && !c.is_rented_out) return false;
      // Lager-biler kan skifte platform (typisk til langtidsleje) — de vises derfor under langtid leje uanset platform-flag,
      // og de vises IKKE under dags leje (GoMore) da de endnu ikke er på GoMore.
      if (isRent && filters.platform === "gomore" && (c.status === "lager" || !c.is_gomore_public)) return false;
      if (isRent && filters.platform === "direct" && c.status !== "lager" && c.is_gomore_public) return false;
      return true;
    });

    const priceFn = isRent ? rentFromPrice : salePrice;
    const returnTs = (c) => (c.rental_return_date ? new Date(c.rental_return_date).getTime() : NaN);

    if (filters.sort === "price_asc") list.sort((a, b) => priceFn(a) - priceFn(b));
    else if (filters.sort === "price_desc") list.sort((a, b) => priceFn(b) - priceFn(a));
    else if (filters.sort === "return_asc") {
      list.sort((a, b) => {
        const ra = returnTs(a), rb = returnTs(b);
        if (isNaN(ra) && isNaN(rb)) return 0;
        if (isNaN(ra)) return 1;
        if (isNaN(rb)) return -1;
        return ra - rb;
      });
    } else if (filters.sort === "return_desc") {
      list.sort((a, b) => {
        const ra = returnTs(a), rb = returnTs(b);
        if (isNaN(ra) && isNaN(rb)) return 0;
        if (isNaN(ra)) return 1;
        if (isNaN(rb)) return -1;
        return rb - ra;
      });
    }
    return list;
  }, [data, filters, isRent]);

  const titleLabel = pageTitle || (isRent ? "Biludlejning" : "Biler til salg");
  const defaultHeading = pageHeading || (isRent ? "Find din næste bil." : "Biler vi gerne ser videre.");
  const defaultSub = pageSub || (isRent
    ? "Hos Mahasa lejer du kvalitetsbiler med personlig service. Vælg dato — vi gør resten."
    : "Nøje udvalgte biler til salg. Personlig overdragelse og fuld historik følger med.");
  const cmsTitle = isRent ? siteInfo.rent_title : siteInfo.sale_title;
  const cmsSub = isRent ? siteInfo.rent_subtitle : siteInfo.sale_subtitle;
  const heading = (cmsTitle && cmsTitle.trim() && !isPlatformLocked) ? cmsTitle : defaultHeading;
  const sub = (cmsSub && cmsSub.trim() && !isPlatformLocked) ? cmsSub : defaultSub;
  const sortOptions = isRent ? SORT_OPTIONS_RENT : SORT_OPTIONS_SALE;

  const activeFilters = [];
  if (filters.cat) activeFilters.push({ key: "cat", label: filters.cat });
  if (filters.fuel) activeFilters.push({ key: "fuel", label: filters.fuel });
  if (filters.trans) activeFilters.push({ key: "trans", label: filters.trans });
  if (isRent && filters.availability) activeFilters.push({ key: "availability", label: filters.availability === "available" ? "Ledige nu" : "Udlejet" });
  if (isRent && !isPlatformLocked && filters.platform) activeFilters.push({ key: "platform", label: filters.platform === "gomore" ? "Dags leje" : "Langtid leje" });
  const activeCount = activeFilters.length + (filters.sort !== "default" ? 1 : 0);
  const resetAll = () => setFilters({ cat: null, fuel: null, trans: null, availability: null, platform: platformLock, sort: "default" });

  return (
    <div className="bg-[#F7F3EC] min-h-screen" data-testid={`public-listing-${type}`}>
      <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="label-tag mb-3">{titleLabel}</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-graphite mb-4">{heading}</h1>
          <p className="text-graphite/75 text-base md:text-lg leading-relaxed whitespace-pre-line">{sub}</p>
        </div>

        {/* DESKTOP filter bar */}
        <div className="hidden lg:flex items-center gap-2 flex-wrap mb-4" data-testid="desktop-filter-bar">
          {data.facets.categories.length > 0 && (
            <FilterDropdown label="Kategori" value={filters.cat} onChange={(v) => setFilters({ ...filters, cat: v })} options={[{ value: null, label: "Alle" }, ...data.facets.categories.map((c) => ({ value: c, label: c }))]} testid="filter-category"/>
          )}
          {data.facets.fuels.length > 0 && (
            <FilterDropdown label="Brændstof" value={filters.fuel} onChange={(v) => setFilters({ ...filters, fuel: v })} options={[{ value: null, label: "Alle" }, ...data.facets.fuels.map((c) => ({ value: c, label: c }))]} testid="filter-fuel"/>
          )}
          {data.facets.transmissions.length > 0 && (
            <FilterDropdown label="Gearkasse" value={filters.trans} onChange={(v) => setFilters({ ...filters, trans: v })} options={[{ value: null, label: "Alle" }, ...data.facets.transmissions.map((c) => ({ value: c, label: c }))]} testid="filter-transmission"/>
          )}
          {isRent && (
            <>
              <FilterDropdown label="Status" value={filters.availability} onChange={(v) => setFilters({ ...filters, availability: v })} options={[
                { value: null, label: "Alle" },
                { value: "available", label: "Ledige nu" },
                { value: "rented", label: "Udlejet" },
              ]} testid="filter-availability"/>
              {!isPlatformLocked && (
                <FilterDropdown label="Lejetype" value={filters.platform} onChange={(v) => setFilters({ ...filters, platform: v })} options={[
                  { value: null, label: "Alle" },
                  { value: "gomore", label: "Dags leje" },
                  { value: "direct", label: "Langtid leje" },
                ]} testid="filter-platform"/>
              )}
            </>
          )}
          <FilterDropdown label="Sortér" value={filters.sort} onChange={(v) => setFilters({ ...filters, sort: v })} options={sortOptions} testid="filter-sort"/>
          {activeCount > 0 && (
            <button onClick={resetAll} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-graphite/70 hover:text-graphite hover:bg-graphite/5 transition-colors" data-testid="filter-reset">
              <X size={13}/> Nulstil ({activeCount})
            </button>
          )}
        </div>

        {/* MOBILE filter bar (always visible header row) */}
        <div className="lg:hidden flex items-center justify-between gap-3 mb-6" data-testid="mobile-filter-bar">
          <button
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-graphite text-white text-sm font-medium shadow-sm"
            data-testid="mobile-filter-open"
          >
            <SlidersHorizontal size={15}/>
            Filtre
            {activeCount > 0 && (
              <span className="bg-copper text-white text-[11px] font-bold rounded-full w-5 h-5 inline-flex items-center justify-center">{activeCount}</span>
            )}
          </button>
          <div className="text-xs text-graphite/55" data-testid="listing-count-mobile">
            {filtered.length} {filtered.length === 1 ? "bil" : "biler"}
          </div>
        </div>

        {/* Active filter pills + count (desktop) */}
        <div className="hidden lg:flex items-center justify-between gap-3 flex-wrap mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            {activeFilters.map((f) => (
              <span key={f.key} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E5DED1] text-xs text-graphite">
                {f.label}
                <button onClick={() => setFilters({ ...filters, [f.key]: null })} className="text-graphite/50 hover:text-graphite" aria-label={`Fjern ${f.label}`}>
                  <X size={11}/>
                </button>
              </span>
            ))}
          </div>
          <div className="text-xs text-graphite/55" data-testid="listing-count">
            {filtered.length} {filtered.length === 1 ? "bil" : "biler"}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-graphite/50">Indlæser biler…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-2xl border border-[#E5DED1]" data-testid="empty-listing">
            <h3 className="font-heading text-2xl text-graphite mb-2">Ingen biler matcher</h3>
            <p className="text-graphite/60 text-sm mb-4">Prøv at fjerne et filter — eller kig forbi senere.</p>
            {activeCount > 0 && (
              <button onClick={resetAll} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-graphite text-white text-sm hover:bg-graphite-light transition-colors">
                Nulstil filtre
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {filtered.map((c) => <CarCard key={c.license_plate} car={c} type={type} />)}
          </div>
        )}
      </section>

      <MobileFilterDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        filters={filters}
        setFilters={setFilters}
        facets={data.facets}
        isRent={isRent}
        sortOptions={sortOptions}
        activeCount={activeCount}
        onReset={resetAll}
        isPlatformLocked={isPlatformLocked}
      />
    </div>
  );
}
